"use client"

import { useState, useEffect } from "react"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import Image from "@/src/components/Image"
import { Pencil, Trash2, AlertCircle } from "lucide-react"
import { AddBranchDialog } from "./components/branch-dialog"
import { branchesAPI } from '@/src/lib/api/branches'
import type { Branch } from '@/src/types/branch'
import { toast } from 'sonner'
import { Alert, AlertDescription } from "@/components/ui/alert"
import { lineChannelsAPI } from '@/src/lib/api/line-channels'
import type { LineChannel } from '@/src/lib/api/line-channels'
import { lineGroupsAPI } from '@/src/lib/api/line-groups'
import type { LineGroupMapping } from '@/src/lib/api/line-groups'
import { messagingAccountsAPI } from '@/src/lib/api/messaging-accounts'
import type { MessagingAccount } from '@/src/lib/api/messaging-accounts'

export default function BranchPage() {
  const [isAddBranchDialogOpen, setIsAddBranchDialogOpen] = useState(false)
  const [isEditBranchDialogOpen, setIsEditBranchDialogOpen] = useState(false)
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null)
  const [branches, setBranches] = useState<Branch[]>([])
  const [lineChannels, setLineChannels] = useState<LineChannel[]>([])
  const [lineGroups, setLineGroups] = useState<LineGroupMapping[]>([])
  const [messagingAccounts, setMessagingAccounts] = useState<MessagingAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [branchData, channels, groups, messaging] = await Promise.allSettled([
        branchesAPI.getAll(),
        lineChannelsAPI.getAll(),
        lineGroupsAPI.getAll(),
        messagingAccountsAPI.getAll(),
      ])
      if (branchData.status === 'fulfilled') setBranches(branchData.value)
      else { console.error('Failed to load branches:', branchData.reason); setError('ไม่สามารถโหลดข้อมูลสาขาได้'); toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูล') }
      if (channels.status === 'fulfilled') setLineChannels(channels.value)
      if (groups.status === 'fulfilled') setLineGroups(groups.value)
      if (messaging.status === 'fulfilled') setMessagingAccounts(messaging.value)
    } finally {
      setLoading(false)
    }
  }

  /** Returns LINE connections scoped to a specific branch. */
  const getLineConnectionsForBranch = (branch: Branch) => {
    // LINE OA and groups are per-business; channels with no branch_id serve all branches of that business
    const oaChannels = lineChannels.filter(c =>
      c.is_active && c.business_id === branch.business_id &&
      (c.branch_id === null || c.branch_id === branch.id)
    )
    const groups = lineGroups.filter(g =>
      g.is_active && g.business_id === branch.business_id &&
      (g.branch_id === null || g.branch_id === branch.id)
    )
    // Messaging accounts (1:1 chat): show if no default branch, or pinned to this branch
    const chatAccounts = messagingAccounts.filter(ma =>
      ma.is_active && (ma.default_branch_id === null || ma.default_branch_id === branch.id)
    )
    return { oaChannels, groups, chatAccounts }
  }

  const handleDeleteBranch = async (branchId: string) => {
    if (!confirm('คุณต้องการลบสาขานี้หรือไม่?')) return

    try {
      await branchesAPI.delete(branchId)
      toast.success('ลบสาขาสำเร็จ')
      loadData()
    } catch (error: any) {
      console.error('Failed to delete branch:', error)
      toast.error(error.response?.data?.error || 'ไม่สามารถลบสาขาได้')
    }
  }

  const handleEditBranch = (branch: Branch) => {
    setSelectedBranch(branch)
    setIsEditBranchDialogOpen(true)
  }

  const handleDialogClose = (dialogType: 'add' | 'edit') => {
    if (dialogType === 'add') {
      setIsAddBranchDialogOpen(false)
    } else {
      setIsEditBranchDialogOpen(false)
      setSelectedBranch(null)
    }
    loadData() // Refresh list
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">สาขา</h1>
          <div className="flex items-center gap-3">
            <Button 
              className="bg-brand-light-red hover:bg-brand-light-red/90 text-white"
              onClick={() => setIsAddBranchDialogOpen(true)}
            >
              เพิ่มสาขา
            </Button>
          </div>
        </div>

        {/* Add Branch Dialog */}
        <AddBranchDialog 
          open={isAddBranchDialogOpen}
          onOpenChange={() => handleDialogClose('add')}
        />

        {/* Edit Branch Dialog */}
        {selectedBranch && (
          <AddBranchDialog 
            open={isEditBranchDialogOpen}
            onOpenChange={() => handleDialogClose('edit')}
            mode="edit"
            branchId={selectedBranch.id}
            initialData={{
              branchName: selectedBranch.branch_name,
              selectedAccounts: selectedBranch.accounts?.map(a => a.id) || []
            }}
          />
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && branches.length === 0 && !error && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 mb-4">ยังไม่มีสาขา</p>
            <Button 
              onClick={() => setIsAddBranchDialogOpen(true)}
              className="bg-brand-light-red hover:bg-brand-light-red/90 text-white"
            >
              เพิ่มสาขาแรก
            </Button>
          </div>
        )}

        {/* Branch Cards */}
        {!loading && branches.length > 0 && (
          <div className="space-y-4">
            {branches.map((branch) => {
              const { oaChannels, groups, chatAccounts } = getLineConnectionsForBranch(branch)
              return (
              <div key={branch.id} className="bg-white rounded-lg shadow-sm p-6">
                {/* Branch Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <h2 className="text-lg font-normal text-gray-900">
                      สาขา <span className="text-brand-blue font-semibold">{branch.branch_name}</span>
                    </h2>
                    {branch.is_main_branch && (
                      <span className="px-3 py-1 text-xs bg-brand-blue/10 text-brand-blue rounded-full font-medium">
                        สาขาหลัก
                      </span>
                    )}
                    {!branch.is_active && (
                      <span className="px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded-full font-medium">
                        ไม่ใช้งาน
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm"
                      className="bg-brand-blue hover:bg-brand-blue/90 text-white flex items-center gap-2"
                      onClick={() => handleEditBranch(branch)}
                    >
                      ตั้งค่าการเชื่อมต่อ
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!branch.is_main_branch && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-brand-red hover:text-brand-red/80 hover:bg-red-50"
                        onClick={() => handleDeleteBranch(branch.id)}
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Branch Info Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${(branch.account_count ?? 0) > 0 ? 'bg-[#06C755]/10' : 'bg-gray-50'}`}>
                    <Image src="/icons/icon-accounts.svg" alt="Accounts" width={24} height={24} />
                    <div>
                      <p className="text-xs text-gray-500">บัญชีธนาคาร</p>
                      <p className={`text-lg font-semibold ${(branch.account_count ?? 0) > 0 ? 'text-[#06C755]' : 'text-gray-900'}`}>
                        {branch.account_count || 0}
                      </p>
                    </div>
                  </div>
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${oaChannels.length > 0 ? 'bg-[#06C755]/10' : 'bg-gray-50'}`}>
                    <Image src="/icons/icon-line-oa.svg" alt="LINE OA" width={24} height={24} />
                    <div>
                      <p className="text-xs text-gray-500">LINE OA</p>
                      <p className={`text-lg font-semibold ${oaChannels.length > 0 ? 'text-[#06C755]' : 'text-gray-400'}`}>
                        {oaChannels.length > 0 ? oaChannels.length : '-'}
                      </p>
                    </div>
                  </div>
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${chatAccounts.length > 0 ? 'bg-[#06C755]/10' : 'bg-gray-50'}`}>
                    <Image src="/icons/icon-line.svg" alt="LINE" width={24} height={24} />
                    <div>
                      <p className="text-xs text-gray-500">THAISLIPCHECK</p>
                      <p className={`text-lg font-semibold ${chatAccounts.length > 0 ? 'text-[#06C755]' : 'text-gray-400'}`}>
                        {chatAccounts.length > 0 ? chatAccounts.length : '-'}
                      </p>
                    </div>
                  </div>
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${groups.length > 0 ? 'bg-[#06C755]/10' : 'bg-gray-50'}`}>
                    <Image src="/icons/icon-line-chat.svg" alt="LINE Chat" width={24} height={24} />
                    <div>
                      <p className="text-xs text-gray-500">LINE กลุ่มแชท</p>
                      <p className={`text-lg font-semibold ${groups.length > 0 ? 'text-[#06C755]' : 'text-gray-400'}`}>
                        {groups.length > 0 ? groups.length : '-'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Branch Details */}
                <div className="mt-4 space-y-2">
                  {branch.branch_code && (
                    <div className="text-sm text-gray-600">
                      รหัสสาขา: <span className="font-medium">{branch.branch_code}</span>
                    </div>
                  )}
                  
                  {/* Bank Accounts List */}
                  {branch.accounts && branch.accounts.length > 0 && (
                    <div className="text-sm text-gray-600">
                      <p className="font-medium mb-1">บัญชีธนาคารที่เชื่อมต่อ:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-500">
                        {branch.accounts.map(account => (
                          <li key={account.id}>
                            {account.account_name} 
                            {account.account_source === 'bank' && account.account_number && ` - ${account.account_number}`}
                            {account.account_source === 'promptpay' && account.promptpay_id && ` - ${account.promptpay_id}`}
                            {account.account_source === 'shop' && account.shop_code && ` - ${account.shop_code}`}
                            {account.account_source === 'truemoney' && account.truemoney_id && ` - ${account.truemoney_id}`}
                            {account.bank_code && ` (${account.bank_code})`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* LINE Connections Detail */}
                  {(oaChannels.length > 0 || chatAccounts.length > 0 || groups.length > 0) && (
                    <div className="text-sm text-gray-600">
                      <p className="font-medium mb-1">LINE เชื่อมต่อ:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-500">
                        {oaChannels.map(ch => (
                          <li key={ch.id}>
                            LINE OA: {ch.channel_name || ch.channel_id}
                            {ch.branch_id === null && <span className="text-gray-400"> (auto)</span>}
                          </li>
                        ))}
                        {chatAccounts.map(ma => (
                          <li key={ma.id}>
                            THAISLIPCHECK: {ma.platform_username || ma.platform_user_id}
                            {ma.default_branch_id === null && <span className="text-gray-400"> (auto)</span>}
                          </li>
                        ))}
                        {groups.length > 0 && (
                          <li>LINE กลุ่มแชท: {groups.length} กลุ่ม</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              )
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
